import React, { useEffect, useRef, memo, useMemo } from "react";
import ApexCharts from "apexcharts";
import { useSelector } from "react-redux";

const GroupedColumnChart = ({
  icon,
  title,
  categories,
  series,
  colors = ["#458276", "#6AA4F7", "#E8C458"],
  yAxisLabel = "in tCO2e",
  columnWidth = "24%",
  min = 0,
  max = undefined,
  tickAmount = undefined,
  strokeWidth = 4,
  xAxisBorderOffsetY = 1,
}) => {
  const chartRef = useRef(null);
  const { user } = useSelector((state) => state.auth);
  const isPermissionCompleted = user?.isCompleted;

  const calculatedMax = useMemo(() => {
    if (max !== undefined) return max;
    let maxVal = 0;
    series?.forEach((s) => {
      s?.data?.forEach((d) => {
        const num = typeof d === "object" ? d?.y || d?.value : Number(d) || 0;
        if (num > maxVal) maxVal = num;
      });
    });
    return maxVal === 0 ? 100 : Math.ceil(maxVal * 1.2);
  }, [series, max]);

  const options = {
    chart: {
      type: "bar",
      height: 250,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    grid: {
      show: false,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: columnWidth,
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: "10px",
        fontWeight: "normal",
        colors: ["#333"],
      },
      formatter: function (val, opts) {
        if (val === null || val === undefined) return "";

        // Prefer original values when provided (we may have bumped tiny values for display)
        const series = opts?.w?.config?.series || [];
        const seriesObj = series[opts.seriesIndex] || {};
        let originalVal;
        if (seriesObj && seriesObj.originalValues) {
          originalVal = seriesObj.originalValues[opts.dataPointIndex];
        } else {
          const dataPoint = seriesObj?.data?.[opts.dataPointIndex];
          originalVal =
            typeof dataPoint === "object"
              ? (dataPoint?.originalValue ?? dataPoint?.y ?? dataPoint?.value)
              : dataPoint;
        }

        // Always show value rounded to 2 decimals
        const num =
          Number(
            originalVal !== undefined && originalVal !== null
              ? originalVal
              : val,
          ) || 0;
        return parseFloat(num.toFixed(2)).toLocaleString();
      },
    },
    stroke: {
      show: strokeWidth > 0,
      width: strokeWidth,
      colors: ["transparent"],
    },
    xaxis: {
      categories: categories,
      axisBorder: {
        show: true,
        color: "#000",
        offsetY: xAxisBorderOffsetY,
      },
      labels: {
        style: {
          fontSize: "12px",
          fontFamily: "Calibri",
          fontWeight: "normal",
        },
      },
    },
    yaxis: {
      min: min,
      max: calculatedMax,
      tickAmount: tickAmount,
      axisBorder: {
        show: true,
        color: "#000",
      },
      title: {
        text: yAxisLabel,
        style: {
          fontSize: "12px",
          fontFamily: "Calibri",
          color: "#000",
          fontWeight: "normal",
        },
      },
      labels: {
        formatter: (val) => val.toLocaleString(),
        style: {
          fontSize: "11px",
          color: "#000",
          fontWeight: "normal",
        },
      },
    },
    fill: {
      opacity: 1,
    },
    colors: colors,
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "13px",
      fontFamily: "Calibri",
      markers: {
        width: 10,
        height: 10,
        radius: 2,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 0,
      },
    },
    tooltip: {
      y: {
        formatter: (val, opts) => {
          // Use original value for tooltip when present
          const series = opts?.w?.config?.series || [];
          const seriesObj = series[opts.seriesIndex] || {};
          let originalVal;
          if (seriesObj && seriesObj.originalValues) {
            originalVal = seriesObj.originalValues[opts.dataPointIndex];
          } else {
            const dataPoint = seriesObj?.data?.[opts.dataPointIndex];
            originalVal =
              typeof dataPoint === "object"
                ? (dataPoint?.originalValue ?? dataPoint?.y ?? dataPoint?.value)
                : dataPoint;
          }
          const display =
            originalVal !== undefined && originalVal !== null
              ? originalVal
              : val;
          return `${display} tCO2e`;
        },
      },
    },
    states: {
      hover: { filter: { type: "none" } },
      active: { filter: { type: "none" } },
    },
  };

  useEffect(() => {
    if (chartRef.current) {
      const chart = new ApexCharts(chartRef.current, {
        ...options,
        series: series,
      });
      chart.render();
      return () => chart.destroy();
    }
  }, [series, categories]);

  return (
    <div className="w-full bg-white border border-[#3DA3BA59] rounded-2xl p-4 flex flex-col gap-4 grouped-column-chart-container">
      {/* Title block */}
      <div className="flex items-center gap-2 min-h-[48px]">
        <span className="text-[#06513D] text-2xl md:text-3xl flex-shrink-0">
          {icon}
        </span>
        <h2 className="text-base capitalize md:text-lg font-semibold text-[#06513D]">
          {title}
        </h2>
      </div>

      {/* Chart container */}
      <div
        ref={chartRef}
        className={`w-full max-w-full ${
          !isPermissionCompleted ? "blur-sm opacity-60 pointer-events-none" : ""
        }`}
      />
    </div>
  );
};

export default memo(GroupedColumnChart);
